import { useEffect, useState, useCallback } from "react";
import UpdatePanel from "@/components/UpdatePanel";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Play, RefreshCcw, Loader2, Download, Zap, AlertTriangle, CheckCircle2, Clock, Search } from "lucide-react";
import ToolLogo from "@/components/ToolLogo";
import StopScraperButton from "@/components/StopScraperButton";
import ScrapeModeSelector from "@/components/ScrapeModeSelector";

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  const units = ["B", "kB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

export default function ScrapeControl() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [maxPages, setMaxPages] = useState("");
  const [withDetails, setWithDetails] = useState(true);
  const [starting, setStarting] = useState(false);
  const [startingDetails, setStartingDetails] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [syncingIndex, setSyncingIndex] = useState(false);
  const [useFlaresolverr, setUseFlaresolverr] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: st }, { data: hist }] = await Promise.all([
        api.get("/scrape/status"),
        api.get("/scrape/details/history", { params: { limit: 8 } }),
      ]);
      setStatus(st);
      setHistory(hist.items || []);
    } catch (err) {
      console.warn("status error", err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const start = async () => {
    setStarting(true);
    try {
      const params = {};
      if (maxPages) params.max_pages = Number(maxPages);
      params.with_details = withDetails;
      params.use_flaresolverr = useFlaresolverr;
      await api.post("/scrape/run", null, { params });
      toast.success("Scraper gestartet");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Konnte Scraper nicht starten";
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  const startDetails = async () => {
    setStartingDetails(true);
    try {
      await api.post("/scrape/details/run", null, {
        params: { kind: "ecu", only_missing: onlyMissing, use_flaresolverr: useFlaresolverr },
      });
      toast.success("Details-Crawl gestartet");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Konnte Details-Crawl nicht starten";
      toast.error(msg);
    } finally {
      setStartingDetails(false);
    }
  };

  const syncSearchIndex = async () => {
    setSyncingIndex(true);
    try {
      const { data } = await api.post("/search-index/sync");
      toast.success(`Such-Index aktualisiert (${data.synced.toLocaleString("de-DE")} Einträge)`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Konnte Such-Index nicht aktualisieren";
      toast.error(msg);
    } finally {
      setSyncingIndex(false);
    }
  };

  return (
    <div data-testid="scrape-page" className="space-y-8">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ToolLogo source="autotuner" className="h-8" />
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D32222]">
            Scrape Control
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Control card */}
        <div className="rounded-xl border border-neutral-800 bg-panel p-6 space-y-5">
          <div className="text-sm uppercase tracking-widest text-neutral-500">
            Neuen Lauf starten
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-1">
              <Label className="text-xs text-neutral-400">Max. Seiten (leer = alle)</Label>
              <Input
                data-testid="max-pages-input"
                type="number"
                min={1}
                value={maxPages}
                onChange={(e) => setMaxPages(e.target.value)}
                placeholder="z. B. 10"
                className="mt-1 bg-neutral-900 border-neutral-800 text-neutral-100 focus-visible:ring-[#D32222]"
              />
            </div>
            <div className="col-span-1 flex items-center gap-3 pt-6">
              <Switch
                data-testid="with-details-switch"
                checked={withDetails}
                onCheckedChange={setWithDetails}
              />
              <Label className="text-sm text-neutral-300">
                Detailseiten mitcrawlen
              </Label>
            </div>
          </div>
          <ScrapeModeSelector direct={useFlaresolverr} onChange={setUseFlaresolverr} disabled={status?.running} testId="scrape-mode-selector" />
          <div className="flex items-center gap-3">
            <Button
              data-testid="start-scrape-btn"
              onClick={start}
              disabled={starting || status?.running}
              className="bg-[#D32222] hover:bg-[#E13636] text-white font-medium"
            >
              {starting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Scraper starten
            </Button>
            <StopScraperButton endpoint="/scrape/stop" running={status?.running} onStopped={load} testId="stop-scrape-btn" />
            <Button
              data-testid="refresh-status-btn"
              variant="outline"
              className="border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-100"
              onClick={load}
            >
              <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              data-testid="sync-index-btn"
              onClick={syncSearchIndex}
              disabled={syncingIndex}
              variant="outline"
              title="Überträgt den aktuellen Datenstand in den Meilisearch-Index für die Sofortsuche"
              className="border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-100"
            >
              {syncingIndex ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Such-Index aktualisieren
            </Button>
            <a
              href={`${api.defaults.baseURL}/scrape/output`}
              data-testid="download-json-link"
              className="ml-auto inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-[#E13636] transition"
            >
              <Download className="w-4 h-4" /> output.json
            </a>
          </div>
        </div>

        {/* Status card */}
        <div className="rounded-xl border border-neutral-800 bg-panel p-6">
          <div className="text-sm uppercase tracking-widest text-neutral-500 mb-4">
            Status
          </div>
          <div className="grid grid-cols-2 gap-4">
            <StatusPill
              label="Zustand"
              value={
                status?.running ? (
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    aktiv (pid {status.pid})
                  </span>
                ) : (
                  <span className="text-neutral-400">idle</span>
                )
              }
              testid="status-running"
            />
            <StatusPill
              label="Einträge in DB"
              value={
                <span
                  className="tabular-nums text-[#E13636]"
                  data-testid="status-db-count"
                >
                  {status?.items_in_db?.toLocaleString("de-DE") ?? "—"}
                </span>
              }
            />
            <StatusPill
              label="output.json"
              value={formatBytes(status?.output_json_size)}
              testid="status-output-size"
            />
            <StatusPill
              label="Letzter Fortschritt"
              value={
                <span className="text-[11px] font-mono text-neutral-400 line-clamp-1">
                  {status?.last_progress || "—"}
                </span>
              }
              testid="status-last-progress"
            />
          </div>
        </div>
      </div>

      <UpdatePanel provider="autotuner" />

      {/* Log */}
      <div className="rounded-xl border border-neutral-800 bg-overlay overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-800">
          <div className="text-xs uppercase tracking-widest text-neutral-500">
            spider log · tail
          </div>
          <div className="text-[10px] text-neutral-600 font-mono">
            /tmp/scraper_full.log
          </div>
        </div>
        <pre
          data-testid="scrape-log"
          className="p-4 text-[11px] leading-relaxed text-neutral-300 font-mono max-h-[400px] overflow-auto whitespace-pre-wrap"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {(status?.log_tail || []).join("\n") || "keine Logs bisher…"}
        </pre>
      </div>

      {/* Details Crawl + Retry Budget History */}
      <div className="rounded-xl border border-neutral-800 bg-panel p-6 space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222] mb-2 flex items-center gap-2">
              <Zap className="w-3 h-3" /> Details-Crawl · Retry Budget
            </div>
            <h2
              className="text-2xl sm:text-3xl text-neutral-100"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              ECU- &amp; Method-Detailseiten nachziehen
            </h2>
            <p className="text-sm text-neutral-400 mt-1 max-w-2xl">
              Nutzt die konfigurierten Rotating-Proxies. Wenn du gerade eben
              schon einen Lauf gefahren hast, checke den Cooldown-Hint unten
              bevor du erneut startest.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                data-testid="only-missing-switch"
                checked={onlyMissing}
                onCheckedChange={setOnlyMissing}
              />
              <Label className="text-sm text-neutral-300">
                nur fehlende crawlen
              </Label>
            </div>
            <Button
              data-testid="start-details-btn"
              onClick={startDetails}
              disabled={startingDetails || status?.running}
              className="bg-[#D32222] hover:bg-[#E13636] text-white font-medium"
            >
              {startingDetails ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Details-Crawl starten
            </Button>
          </div>
        </div>

        {/* History */}
        <div data-testid="details-history">
          {history.length === 0 ? (
            <div className="text-sm text-neutral-500">
              Noch keine Details-Crawl-Läufe.
            </div>
          ) : (
            <div className="space-y-2">
              {history.map((h) => (
                <HistoryRow key={h.id} h={h} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-neutral-800 bg-overlay/40 p-3"
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-sm text-neutral-100">{value}</div>
    </div>
  );
}

function HistoryRow({ h }) {
  const queued = h.queued || 0;
  const ok = h.success_2xx || 0;
  const failed = h.retry_max_reached || 0;
  const started = h.started_at
    ? new Date(h.started_at).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
  const next = h.next_retry_at ? new Date(h.next_retry_at) : null;
  const cooldownDone = next ? next.getTime() < Date.now() : true;

  const badgeIcon =
    h.fail_ratio >= 0.2 ? (
      <AlertTriangle className="w-3.5 h-3.5" />
    ) : h.fail_ratio > 0 ? (
      <Clock className="w-3.5 h-3.5" />
    ) : (
      <CheckCircle2 className="w-3.5 h-3.5" />
    );
  const badgeCls =
    h.fail_ratio >= 0.2
      ? "text-rose-300 border-rose-500/40 bg-rose-500/10"
      : h.fail_ratio > 0
      ? "text-amber-300 border-amber-500/40 bg-amber-500/10"
      : "text-emerald-300 border-emerald-500/40 bg-emerald-500/10";

  return (
    <div
      data-testid={`history-row-${h.id}`}
      className="grid grid-cols-1 md:grid-cols-[130px_1fr_auto] gap-4 border border-neutral-800 rounded-lg p-3 bg-overlay/30 items-center"
    >
      <div>
        <div className="text-xs text-neutral-400">{started}</div>
        <div className="text-[10px] font-mono text-neutral-600">
          {h.detail_kind || h.kind_run || "details"}
          {h.only_missing ? " · only_missing" : ""}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MiniBudget label="Queued" value={queued} testid={`hist-${h.id}-queued`} />
        <MiniBudget
          label="OK"
          value={ok}
          accent="text-emerald-400"
          testid={`hist-${h.id}-ok`}
        />
        <MiniBudget
          label="Aufgegeben"
          value={failed}
          accent={failed > 0 ? "text-rose-400" : "text-neutral-500"}
          testid={`hist-${h.id}-failed`}
        />
        <MiniBudget
          label="Sekunden"
          value={Math.round(h.elapsed_seconds || 0)}
          testid={`hist-${h.id}-elapsed`}
        />
      </div>

      <div className="flex flex-col items-end gap-1">
        <span
          className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest border rounded px-2 py-1 ${badgeCls}`}
          data-testid={`hist-${h.id}-badge`}
        >
          {badgeIcon}
          {h.cooldown_hours === 0
            ? "Bereit"
            : cooldownDone
            ? "Cooldown vorbei"
            : `${h.cooldown_hours}h Cooldown`}
        </span>
        <div className="text-[10px] text-neutral-500 text-right max-w-[220px]">
          {h.cooldown_hint}
        </div>
      </div>
    </div>
  );
}

function MiniBudget({ label, value, accent = "text-neutral-100", testid }) {
  return (
    <div className="text-center">
      <div
        data-testid={testid}
        className={`text-lg tabular-nums ${accent}`}
        style={{ fontFamily: "'Orbitron', sans-serif" }}
      >
        {value?.toLocaleString?.("de-DE") ?? value}
      </div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">
        {label}
      </div>
    </div>
  );
}
